import React from 'react';

import { Button, Loader, Stack, Text } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';

import { plum } from '@/styles/colors';

import { BatchJobStatus } from '@/types/batchDownload';
import CustomModal from '@/ui/Modal';

interface BatchDownloadState {
  jobId: number;
  status: BatchJobStatus;
  errorMessage: string;
  showModal: boolean;
  isDownloading: boolean;
}

interface BatchDownloadModalProps {
  batchDownloadState: BatchDownloadState;
  setBatchDownloadState: React.Dispatch<React.SetStateAction<BatchDownloadState>>;
  onDownload: () => Promise<void>;
  onClose: () => void;
  notDownloadedCount: number;
  title?: string;
  downloadingMessage?: string;
  successMessage?: string;
}

export const BatchDownloadModal: React.FC<BatchDownloadModalProps> = ({
  batchDownloadState,
  onDownload,
  onClose,
  notDownloadedCount,
  title,
  downloadingMessage,
  successMessage
}) => {
  const getModalTitle = () => {
    if (title) {
      switch (batchDownloadState.status) {
        case BatchJobStatus.INITIALIZED:
        case BatchJobStatus.PROCESSING:
          return `Preparing ${title}`;
        case BatchJobStatus.SUCCESS:
          return `${title} Ready`;
        case BatchJobStatus.FAILED:
          return `${title} Failed`;
        default:
          return title;
      }
    }

    switch (batchDownloadState.status) {
      case BatchJobStatus.INITIALIZED:
      case BatchJobStatus.PROCESSING:
        return 'Preparing Reports';
      case BatchJobStatus.SUCCESS:
        return 'Download Ready';
      case BatchJobStatus.FAILED:
        return 'Download Failed';
      default:
        return 'Batch Download';
    }
  };

  const renderModalContent = () => {
    switch (batchDownloadState.status) {
      case BatchJobStatus.INITIALIZED:
      case BatchJobStatus.PROCESSING:
        return (
          <>
            <Text size="md" mt="xl">
              {downloadingMessage ||
                `Processing ${notDownloadedCount} reports. This may take a few minutes...`}
            </Text>
            <Loader color={plum[6]} type="dots" />
          </>
        );

      case BatchJobStatus.SUCCESS:
        return (
          <>
            <Text size="md" fw={500} mt="xl">
              {successMessage || 'Reports are ready for download!'}
            </Text>
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={onDownload}
              loading={batchDownloadState.isDownloading}
              mt="md">
              Download ZIP File
            </Button>
          </>
        );

      case BatchJobStatus.FAILED:
        return (
          <>
            <Text color="red" size="sm" mt="xl">
              {batchDownloadState.errorMessage ||
                'Something went wrong while preparing the reports.'}
            </Text>
            <Button variant="outline" onClick={onClose} mt="md">
              Close
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <CustomModal
      modalProps={{
        opened: batchDownloadState.showModal,
        onClose: onClose,
        size: 'md',
        closeOnClickOutside: false,
        closeOnEscape: false
      }}
      header={{ title: getModalTitle() }}>
      <Stack align="center">{renderModalContent()}</Stack>
    </CustomModal>
  );
};
